import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import {
  Container, Typography, Box, Button, IconButton, Card, CardContent,
  Divider, Chip, Grid, CircularProgress, Dialog, DialogTitle,
  DialogContent, TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon, Share as ShareIcon,
  LocationOn, ArrowBackIos, ArrowForwardIos
} from '@mui/icons-material';
import PropertyDeleteButton from '../components/Properties/PropertyDeleteButton';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Debug log the ID and environment variables
  useEffect(() => {
    console.log('Property ID from URL params:', id);
    console.log('Environment Variables:', {
      supabaseUrl: process.env.REACT_APP_SUPABASE_URL,
      nodeEnv: process.env.NODE_ENV,
      publicUrl: process.env.PUBLIC_URL
    });
  }, [id]);
  
  // Fetch property data
  useEffect(() => {
    // Check for missing ID
    if (!id) {
      console.error('No property ID found in URL');
      setError('No property ID specified');
      setLoading(false);
      return;
    }
    
    const fetchProperty = async () => {
      try {
        setLoading(true);
        const propertyId = parseInt(id, 10);
        console.log('Fetching property with ID:', propertyId);
        
        if (isNaN(propertyId)) {
          throw new Error('Invalid property ID');
        }
        
        const { data: propertyData, error: propertyError } = await supabase
          .from('properties')
          .select('*')
          .eq('id', propertyId)
          .single();

        console.log('Property query result:', { 
          data: propertyData, 
          error: propertyError,
          rawData: JSON.stringify(propertyData, null, 2)
        });

        if (propertyError) {
          console.error('Supabase error:', propertyError);
          throw propertyError;
        }
        
        if (!propertyData) {
          throw new Error('No property data returned');
        }
        
        // Process images
        const processedProperty = {
          ...propertyData,
          images: (propertyData.images || []).map(img => {
            // If it's already a full URL, return as is
            if (img.startsWith('http')) return img;
            
            // Handle different possible image path formats
            let cleanPath = img.replace(/^\//, ''); // Remove leading slash if present
            
            // If the image path doesn't include the bucket name, add it
            if (!cleanPath.startsWith('property-images/')) {
              cleanPath = `property-images/${cleanPath}`;
            }
            
            const url = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${cleanPath}`;
            console.log('Generated image URL:', url);
            return url;
          })
        };
        
        console.log('Processed property data:', processedProperty);
        
        setProperty(processedProperty);
        document.title = `${processedProperty.title} | ${processedProperty.city}, ${processedProperty.state}`;
        
      } catch (err) {
        console.error('Error loading property:', {
          message: err.message,
          status: err.status,
          statusText: err.statusText,
          stack: err.stack,
          response: err.response,
          config: err.config
        });
        setError(`Failed to load property: ${err.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  // Debug log property data
  useEffect(() => {
    if (property) {
      console.log('Property data in PropertyDetail:', property);
      console.log('Property owner ID:', property.owner_id);
    }
  }, [property]);

  // Debug log property location data
  useEffect(() => {
    if (property) {
      console.log('Property location data:', {
        address: property.address,
        city: property.city,
        state: property.state,
        rawCity: JSON.stringify(property.city),
        rawState: JSON.stringify(property.state)
      });
    }
  }, [property]);

  // Helper functions
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const nextImage = () => {
    if (!property?.images?.length) return;
    setCurrentImageIndex(prev => 
      prev === property.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (!property?.images?.length) return;
    setCurrentImageIndex(prev => 
      prev === 0 ? property.images.length - 1 : prev - 1
    );
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
        <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  // Not found state
  if (!property) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary">
          Property not found
        </Typography>
        <Button onClick={() => navigate('/properties')} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Browse Properties
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button 
        onClick={() => navigate(-1)} 
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3 }}
      >
        Back to Results
      </Button>

      {/* Property Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {property.title}
          </Typography>
          <Box>
            <IconButton 
              onClick={() => setIsFavorite(!isFavorite)}
              color={isFavorite ? 'error' : 'default'}
            >
              {isFavorite ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
            </IconButton>
            <IconButton color="primary" onClick={() => window.print()}>
              <ShareIcon />
            </IconButton>
            <PropertyDeleteButton property={property} />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LocationOn color="action" sx={{ mr: 0.5 }} />
          <Typography variant="subtitle1" color="text.secondary">
            {[property.address, property.city, property.state]
              .filter(part => part && part.trim() !== '')
              .map(part => part.trim().replace(/^,|,$/g, ''))
              .join(', ')
            }
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Chip 
            label={property.status?.replace('-', ' ')} 
            color={property.status === 'for-sale' ? 'primary' : 'secondary'}
            size="small"
          />
          <Typography variant="h5" color="primary" fontWeight={700}>
            {formatPrice(property.price)}
          </Typography>
        </Box>
      </Box>

      {/* Image Gallery */}
      <Box sx={{ mb: 4 }}>
        {property.images?.length > 0 ? (
          <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: '#f5f5f5', minHeight: '300px' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100%',
              height: '500px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {(() => {
                const imageUrl = property.images[currentImageIndex];
                console.log(`Loading image ${currentImageIndex + 1}/${property.images.length}:`, imageUrl);
                
                return (
                  <img
                    src={property.images[currentImageIndex]}
                    alt={`${property.title} - ${currentImageIndex + 1}`}
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'opacity 0.3s ease-in-out'
                    }}
                    onLoad={() => console.log(`Image loaded: ${property.images[currentImageIndex]}`)}
                    onError={(e) => {
                      console.error(`Failed to load image: ${property.images[currentImageIndex]}`);
                      e.target.onerror = null;
                      e.target.src = 'https://placehold.co/800x500?text=Image+Not+Available';
                    }}
                  />
                );
              })()}
              
              {/* Image counter */}
              <Box sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 4,
                fontSize: '0.875rem'
              }}>
                {currentImageIndex + 1} / {property.images.length}
              </Box>
              
              {property.images.length > 1 && (
                <>
                  <IconButton
                    onClick={prevImage}
                    sx={{ 
                      position: 'absolute', 
                      left: 16, 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                    }}
                  >
                    <ArrowBackIos />
                  </IconButton>
                  
                  <IconButton
                    onClick={nextImage}
                    sx={{ 
                      position: 'absolute', 
                      right: 16, 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                    }}
                  >
                    <ArrowForwardIos />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            height: 300, 
            bgcolor: '#f5f5f5', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            borderRadius: 2
          }}>
            <Typography color="text.secondary">No images available</Typography>
          </Box>
        )}
      </Box>

      {/* Property Details */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Property Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">Property Type</Typography>
                  <Typography variant="body1" textTransform="capitalize">
                    {property.property_type || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Typography variant="body1" textTransform="capitalize">
                    {property.status?.replace('-', ' ') || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {property.description || 'No description available'}
                  </Typography>
                </Grid>
              </Grid>
              
              {property.amenities && property.amenities.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Features & Amenities</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {property.amenities.map((amenity, index) => (
                      <Chip 
                        key={index} 
                        label={amenity} 
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Contact Agent
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Button 
                fullWidth 
                variant="contained" 
                color="primary" 
                size="large"
                component="a"
                href={`mailto:lewmaxsuppliers@gmail.com?subject=Inquiry about ${encodeURIComponent(property.title)}&body=Hello,%0D%0A%0D%0AI am interested in the property at ${encodeURIComponent(property.address)}. Please provide more details.`}
                sx={{ textDecoration: 'none', mb: 2 }}
              >
                Email Agent
              </Button>
              
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                size="large"
                component="a"
                href="tel:0723294696"
                sx={{ textDecoration: 'none' }}
              >
                Call: 0723294696
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PropertyDetail;
